import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { Loader2, Gamepad2 } from 'lucide-react';
import { toast } from 'sonner';

export default function GamerTagPrompt() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [tag, setTag] = useState('');
  const [loading, setLoading] = useState(false);

  // Treat localStorage marker as fallback to avoid re-prompting on refresh latency
  const localMarker = typeof window !== 'undefined' && window.localStorage.getItem('fz_gamertag_set');
  const needsTag = user && user !== false && !user.gamer_tag && !localMarker;

  const submit = async (e) => {
    e.preventDefault();
    if (!tag.trim()) return;
    setLoading(true);
    try {
      await api.patch('/users/me', { gamer_tag: tag.trim(), name: tag.trim() });
      try { window.localStorage.setItem('fz_gamertag_set', '1'); } catch {}
      await refresh();
      toast.success(t('gamertag.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={!!needsTag}>
      <DialogContent
        className="glass-strong border-white/10 text-white max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center mb-2">
            <Gamepad2 className="w-6 h-6 text-black" />
          </div>
          <DialogTitle className="font-display text-2xl uppercase tracking-tighter">
            {t('gamertag.title')}
          </DialogTitle>
          <DialogDescription className="text-white/70">{t('gamertag.desc')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-white/80">{t('gamertag.field')}</Label>
            <Input
              data-testid="gamertag-input"
              required
              autoFocus
              maxLength={40}
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="ApexHunter_FR"
              className="bg-black/40 border-white/10 text-white placeholder:text-white/30"
            />
            <div className="text-[11px] text-white/40 mt-1">{t('gamertag.hint')}</div>
          </div>
          <Button
            type="submit"
            data-testid="gamertag-submit"
            disabled={loading || !tag.trim()}
            className="w-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold uppercase tracking-wide rounded-full"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('gamertag.save')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
